import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, ArrowRight, CheckCircle2, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, token, refreshUser, API } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (user?.email_verified) {
      navigate('/app');
    }
  }, [user, navigate]);

  const handleVerify = async () => {
    if (code.length < 6) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      setVerified(true);
      await refreshUser();
      setTimeout(() => navigate('/app'), 2000);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    setResent(false);
    try {
      const res = await fetch(`${API}/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.email_sent) {
        setResent(true);
        setTimeout(() => setResent(false), 4000);
      } else {
        setError('Failed to send email. Please try again.');
      }
    } catch (e) { setError('Failed to resend'); }
    setResending(false);
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="font-heading font-black text-2xl text-white mb-2">Email Verified!</h2>
          <p className="text-sm text-zinc-500">Redirecting to your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 honeycomb-bg opacity-20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm text-center"
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
          <Inbox className="w-8 h-8 text-amber-500" />
        </div>

        <h1 className="font-heading font-black text-2xl text-white mb-2">Check Your Inbox</h1>
        <p className="text-sm text-zinc-500 mb-1">
          We sent a 6-digit verification code to
        </p>
        <p className="text-sm text-white font-medium mb-6">{user?.email}</p>

        {/* Email sent confirmation */}
        <div className="glass-card rounded-lg px-4 py-3 mb-6 border border-white/5">
          <div className="flex items-center justify-center gap-2">
            <Mail className="w-4 h-4 text-amber-500" />
            <p className="text-xs text-zinc-400">Enter the code from your email below</p>
          </div>
        </div>

        {error && (
          <div data-testid="verify-error" className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400 mb-4">
            {error}
          </div>
        )}

        {resent && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400 mb-4">
            New code sent! Check your inbox.
          </div>
        )}

        <div className="flex justify-center mb-6">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={setCode}
            data-testid="verify-otp-input"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="bg-[#121214] border-white/10 text-white w-11 h-12" />
              <InputOTPSlot index={1} className="bg-[#121214] border-white/10 text-white w-11 h-12" />
              <InputOTPSlot index={2} className="bg-[#121214] border-white/10 text-white w-11 h-12" />
              <InputOTPSlot index={3} className="bg-[#121214] border-white/10 text-white w-11 h-12" />
              <InputOTPSlot index={4} className="bg-[#121214] border-white/10 text-white w-11 h-12" />
              <InputOTPSlot index={5} className="bg-[#121214] border-white/10 text-white w-11 h-12" />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <button
          data-testid="verify-submit-btn"
          onClick={handleVerify}
          disabled={code.length < 6 || loading}
          className="honey-btn w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
        >
          {loading ? 'Verifying...' : 'Verify Email'}
          {!loading && <ArrowRight className="w-4 h-4" />}
        </button>

        <div className="flex items-center justify-center gap-4">
          <button
            data-testid="verify-resend-btn"
            onClick={handleResend}
            disabled={resending}
            className="text-xs text-zinc-500 hover:text-amber-500 flex items-center gap-1 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Sending...' : 'Resend Code'}
          </button>
          <button
            data-testid="verify-skip-btn"
            onClick={() => navigate('/app')}
            className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
          >
            Skip for now
          </button>
        </div>

        <p className="text-[10px] text-zinc-700 mt-6">
          Don't see it? Check your spam folder.
        </p>
      </motion.div>
    </div>
  );
}
